import { isAnyOf, UnknownAction } from '@reduxjs/toolkit'
import { createReducer } from '@reduxjs/toolkit/react'
import { CaseWorkerRole, CaseWorkerStatus } from '@repo/shared/dtos/caseWorker'
import { Language, LifeCycleStage } from '@repo/shared/dtos/organization'
import { AsyncData, DataStatus } from '@repo/ui/utils'

import { storeActions, sudoStoreActions } from '@/store/actions.ts'
import { PromptTemplate } from '@/store/admin'
import { FeatureFlagForOrganization } from '@/store/feature-flags/types'

export type SudoContextOrganization = {
  lifeCycleStage: LifeCycleStage
  featureFlags: FeatureFlagForOrganization[]
  id: string
  name: string
  url?: string
}

export type SudoContextPrompt = {
  defaultPrompt: string
  codePrompt: string
  organizationPrompts: {
    organizationId: string
    promptTemplate: PromptTemplate
  }[]
}

export type SudoContextUser = {
  disabled: boolean
  email: string
  firstName: string
  id: string
  language?: Language
  lastName: string
  organizationIds: string[]
  superAdmin: boolean
}

export type SudoContextCaseWorker = {
  firstName: string
  id: string
  lastName: string
  organizationId: string
  role: CaseWorkerRole
  status: CaseWorkerStatus
}

export type SudoState = {
  caseWorkers: AsyncData<SudoContextCaseWorker[]> // The caseWorkers are related to the currentUser
  currentUser: SudoContextUser | undefined
  organizations: AsyncData<SudoContextOrganization[]>
  users: AsyncData<SudoContextUser[]>
}

export const sudoInitialState: SudoState = {
  caseWorkers: DataStatus.Uninitialized,
  currentUser: undefined,
  organizations: DataStatus.Uninitialized,
  users: DataStatus.Uninitialized,
}

const updateUsers = (state: SudoState, user: SudoContextUser) => {
  if (DataStatus.isFulfilled(state.users))
    state.users = state.users.map((v) => (v.id === user.id ? user : v))
}

export const sudoReducer: ((state: SudoState | undefined, action: UnknownAction) => SudoState) & {
  getInitialState: () => SudoState
} = createReducer(sudoInitialState, (builder) => {
  builder.addCase(storeActions.initOrganization.pending, () => {
    return sudoInitialState
  })

  builder
    .addCase(sudoStoreActions.initSudo.pending, (state) => {
      state.organizations = DataStatus.Loading
      state.users = DataStatus.Loading
    })
    .addCase(sudoStoreActions.initSudo.rejected, (state) => {
      state.organizations = DataStatus.Error
      state.users = DataStatus.Error
    })
    .addCase(
      sudoStoreActions.initSudo.fulfilled,
      (state, { payload: { organizations, users } }) => {
        state.users = users
        state.organizations = organizations
      },
    )
    .addCase(sudoStoreActions.user.create.fulfilled, (state, { payload: { users } }) => {
      state.users = users
    })
    .addCase(
      sudoStoreActions.organization.hardDeleteOne.fulfilled,
      (
        state,
        {
          meta: {
            arg: { organizationId },
          },
        },
      ) => {
        if (!DataStatus.isFulfilled(state.organizations)) return
        state.organizations = state.organizations.filter((org) => org.id !== organizationId)
      },
    )
    .addCase(
      sudoStoreActions.caseWorker.updateRole.fulfilled,
      (state, { payload: { caseWorker } }) => {
        if (!DataStatus.isFulfilled(state.caseWorkers)) return
        state.caseWorkers = state.caseWorkers.map((c) => {
          if (c.id === caseWorker.id) {
            return {
              ...c,
              role: caseWorker.role,
            }
          }
          return c
        })
      },
    )
  builder.addCase(
    sudoStoreActions.user.select.fulfilled,
    (
      state,
      {
        meta: {
          arg: { userId },
        },
        payload: { caseWorkers },
      },
    ) => {
      if (!DataStatus.isFulfilled(state.users)) return
      state.caseWorkers = caseWorkers
      state.currentUser = state.users.find((v) => v.id === userId)
    },
  )

  builder.addMatcher(
    isAnyOf(
      sudoStoreActions.organization.create.fulfilled,
      sudoStoreActions.organization.update.fulfilled,
    ),
    (state, { payload: { organizations } }) => {
      state.organizations = organizations
    },
  )

  builder.addMatcher(
    isAnyOf(
      sudoStoreActions.user.enable.fulfilled,
      sudoStoreActions.user.disable.fulfilled,
      sudoStoreActions.user.update.fulfilled,
    ),
    (state, { payload: { user } }) => {
      updateUsers(state, user)
    },
  )
})
